import React, { createContext, useContext, ReactNode } from "react";

/**
 * Creates a context specifically for slices.
 * @template T - The type of the context value.
 * @param sliceName - The name of the slice (used in error messages).
 * @param defaultValue - The default value for the context (used during SSR or if provider is missing).
 */
export function createSliceContext<T>(sliceName: string, defaultValue: T) {
  const SliceContext = createContext<T | undefined>(undefined);

  const useSliceContext = (): T => {
    const context = useContext(SliceContext);
    if (!context) {
      if (typeof window === "undefined") {
        // Return default value during SSR or if provider is not found
        return defaultValue;
      }
      throw new Error(
        `use${sliceName}SliceContext must be used within a ${sliceName}SliceProvider.`
      );
    }
    return context;
  };

  const SliceProvider: React.FC<{ children: ReactNode; value: T }> = ({
    children,
    value,
  }) => {
    return (
      <SliceContext.Provider value={value || defaultValue}>
        {children}
      </SliceContext.Provider>
    );
  };

  return { Provider: SliceProvider, useContext: useSliceContext };
}
